import React, { useMemo } from 'react';
import Select, {
  components,
  OptionProps,
  GroupBase,
  DropdownIndicatorProps,
  ClearIndicatorProps,
  NoticeProps,
  Props as SelectProps,
} from 'react-select';
import { CheckIcon, ChevronDown, PackageOpen, X } from 'lucide-react';
import Option from '@/components/form/types/Option.ts';
import {
  getSelectStyles,
  SelectControlSize,
} from '@/components/form/_primitives/primitiveSelectStyles.ts';
import {
  optionAriaLabel,
  renderOptionLabel,
} from '@/components/form/_primitives/optionLabels.ts';
import {
  MOBILE_SELECT_SEARCH_THRESHOLD,
  useMobileSelectMenu,
} from '@/components/form/_primitives/mobileSelectMenu.ts';

type PrimitiveSingleSelectProps = {
  options: Option[];
  value?: string | object;
  onChange?: (value: string | object | undefined) => void;
  error?: string;
  id?: string;
  valueObjectComparator?: (
    valueOption: string | object,
    value: string | object | undefined,
  ) => boolean;
  /** 30 px zamiast 34 px — wyłącznie dla kontrolek paska narzędzi tabeli. */
  controlSize?: SelectControlSize;
} & Omit<
  SelectProps<Option, false>,
  'value' | 'onChange' | 'options' | 'isMulti'
>;

const PrimitiveSingleSelect = ({
  options,
  value,
  onChange,
  error,
  id,
  valueObjectComparator,
  controlSize = 'default',
  ...props
}: PrimitiveSingleSelectProps) => {
  const optionValue = useMemo(() => {
    if (valueObjectComparator === undefined) {
      return options.find((option) => option.value === value) ?? null;
    } else if (value !== undefined) {
      return (
        options.find((option) => valueObjectComparator(option.value, value)) ??
        null
      );
    }
    return null;
  }, [options, value, valueObjectComparator]);

  const mobileMenu = useMobileSelectMenu({
    title:
      props.mobileMenuTitle ??
      (typeof props.placeholder === 'string' ? props.placeholder : undefined),
    searchable: options.length >= MOBILE_SELECT_SEARCH_THRESHOLD,
  });

  const isMobileMenu = mobileMenu !== null;
  const styles = useMemo(
    () =>
      getSelectStyles<false>(error, controlSize, { mobileMenu: isMobileMenu }),
    [error, controlSize, isMobileMenu],
  );

  return (
    <Select<Option, false>
      {...props}
      inputId={id ?? props.name}
      placeholder={props.placeholder ?? 'Wybierz..'}
      options={options}
      classNamePrefix="custom-select"
      isSearchable
      isMulti={false}
      value={optionValue}
      onChange={(selectedOption) => {
        onChange?.(selectedOption?.value);
        mobileMenu?.onSelect();
      }}
      getOptionLabel={props.getOptionLabel ?? optionAriaLabel}
      formatOptionLabel={props.formatOptionLabel ?? renderOptionLabel}
      menuPortalTarget={document.body}
      menuPosition="fixed"
      menuShouldBlockScroll={true}
      components={{
        Option: DefaultCustomOption,
        DropdownIndicator: CustomDropdownIndicator,
        ClearIndicator: CustomClearIndicator,
        NoOptionsMessage: CustomNoOptions,
        ...props.components,
        ...mobileMenu?.components,
      }}
      styles={styles}
      {...mobileMenu?.selectProps}
    />
  );
};

const DefaultCustomOption = (
  props: OptionProps<Option, false, GroupBase<Option>>,
) => {
  return (
    <components.Option {...props}>
      <div className="flex items-center justify-between gap-2">
        <label className="text-sm leading-4">{props.children}</label>
        {props.isSelected && (
          <CheckIcon size={14} className="text-base-text" strokeWidth={3} />
        )}
      </div>
    </components.Option>
  );
};

const CustomDropdownIndicator = (
  props: DropdownIndicatorProps<Option, false>,
) => {
  return (
    <components.DropdownIndicator {...props}>
      <ChevronDown
        className="text-neutral-600 hover:text-neutral-900"
        size={20}
        strokeWidth={2}
      />
    </components.DropdownIndicator>
  );
};

const CustomClearIndicator = (props: ClearIndicatorProps<Option, false>) => {
  return (
    <components.ClearIndicator {...props}>
      <X
        className="text-neutral-400 hover:cursor-pointer hover:text-neutral-600"
        size={16}
      />
    </components.ClearIndicator>
  );
};

const CustomNoOptions = (props: NoticeProps<Option, false>) => {
  return (
    <components.NoOptionsMessage {...props}>
      <div className="flex flex-row items-center gap-1 text-sm text-neutral-600">
        <PackageOpen className="size-4 min-w-4 text-neutral-400" />
        <p className="truncate text-sm">Brak opcji</p>
      </div>
    </components.NoOptionsMessage>
  );
};

export default PrimitiveSingleSelect;
